import { isFulfilled } from "@reduxjs/toolkit";
import { format, isAfter, isEqual } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getCoreUserFullName } from "../../../common/utils/users";
import {
  AccumDateData,
  AccumulationCalculationMultiple,
  AccumulationsForEmploymentFilters,
  AccumulationsForEmploymentResponse,
  KtaAvg,
  SalaryPerDate,
} from "../../employment/employmentApi";
import { fetchAccumulationsForEmployment } from "../../employment/employmentSlice";
import {
  fetchExpertUsers,
  selectAllExpertUsers,
} from "../../user/expertUsersSlice";
import {
  BulkSalaryIncreaseFormValues,
  EmploymentSalaryRow,
  GroupedEmploymentSalaryRow,
  GroupedSalaryTableRow,
  KtaRow,
  SalaryIncreaseStatus,
  SalaryTableRow,
  SalaryTableRows,
  SalaryWarning,
  SalaryWarningType,
  ShiftSalaryRow,
} from "../types/common";
import { Kta, SalaryRow, SalaryRows } from "../types/queries";
import { BULK_SALARY_INCREASE_TARGETS } from "./constants";
import {
  applyKtaPercentageIncrease,
  applyPercentageIncrease,
  isEmploymentSalary,
  isEmploymentSalaryTableRow,
  isGroupedEmploymentSalaryRow,
  isKtaRow,
  isShiftSalaryTableRow,
  flatMapAsSet,
  latestAccumRow,
  resolveKtaQueryEndDate,
  rowToId,
} from "./helpers";
import {
  BulkSalaryIncreaseFilterValues,
  GetUniqueSalaryKeysCallback,
  GetUniqueSalaryKeysCallbackType,
  KtaComponentRow,
  SalaryComponentRow,
} from "./types";

type Props = {
  filterValues: BulkSalaryIncreaseFilterValues;
};

// ensure numbers like 1.005 round correctly
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/EPSILON
const round = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;
const roundKta = (num: number) => Math.round((num + Number.EPSILON) * 100000000) / 100000000;

const resolveSalaryKeys = (item: SalaryPerDate) => {
  let keys = Object.keys(item.salary);
  const zoned = item.zonedSalary;
  // console.log("zoned", zoned)
  if (zoned) {
    const zones = Object.keys(zoned);
    // console.log("zoned got zones", zones)
    // make heads of format "zonename salary_type"
    const vals = zones.map((z) => Object.keys(zoned[z]).map(zs => z + " " + zs)).flat();
    // console.log("zoned got vals", vals)
    keys = keys.concat(vals);
  }
  return keys;
};

/**
 * Scales the originalValue to match the wished kta
 */
const applyKtaOngoingComponentAdjustment =
    (
        originalValue: number,
        originalHours: number,
        component: SalaryComponentRow | KtaComponentRow,
    ): number[] => {
      // override current salary with new value
      if (component.changeSalary && component.changeSalary.trim() !== "") {
        const override = parseFloat(component.changeSalary);
        const newValue = originalHours * override;
        if (!isNaN(newValue)) {
          return [roundKta(newValue), originalHours];
        }
      }
      // increase current salary by percentage
      if (
          component.increasePercentage &&
          component.increasePercentage.trim() !== ""
      ) {
        const percentage = Number(component.increasePercentage);
        if (!isNaN(percentage)) {
          return [
            applyPercentageIncrease(originalValue, percentage),
            originalHours,
          ];
        }
      }
      // increase current salary by amount
      if (component.increaseAmount && component.increaseAmount.trim() !== "") {
        const amount = Number(component.increaseAmount);
        if (!isNaN(amount)) {
          const original = originalValue / originalHours;
          const newPercentage = amount + original;
          const newValue = newPercentage * originalHours;
          return [roundKta(newValue), originalHours];
        }
      }

      // if something went wrong, return original value
      return [originalValue, originalHours];
    }

const applyKtaComponentAdjustment =
    (
        originalKta: number,
        component: SalaryComponentRow | KtaComponentRow,
    ): number => {
      // override current salary with new value
      if (component.changeSalary && component.changeSalary.trim() !== "") {
        const override = parseFloat(component.changeSalary);
        const newValue = originalKta * override;
        if (!isNaN(newValue)) {
          return roundKta(newValue);
        }
      }
      // increase current salary by percentage
      if (
          component.increasePercentage &&
          component.increasePercentage.trim() !== ""
      ) {
        const percentage = Number(component.increasePercentage);
        if (!isNaN(percentage)) {
          return applyKtaPercentageIncrease(originalKta, percentage);
        }
      }
      // increase current salary by amount
      if (component.increaseAmount && component.increaseAmount.trim() !== "") {
        const amount = Number(component.increaseAmount);
        if (!isNaN(amount)) {
          const newValue = originalKta + amount;
          return roundKta(newValue);
        }
      }

      // if something went wrong, return original value
      return originalKta;
    }


const useBulkSalaryData = ({ filterValues }: Props) => {
  const dispatch = useAppDispatch();
  const workTitleTranslations = useAppSelector(
    (state) => state.localized.workTitle,
  );
  const salaryTranslations = useAppSelector(
    (state) => state.localized.salaryComponent,
  );
  const experts = useAppSelector(selectAllExpertUsers);

  const isKta = filterValues.target === BULK_SALARY_INCREASE_TARGETS.KTA;
  useEffect(() => {
    dispatch(fetchExpertUsers({}));
  }, [dispatch]);

  // table data
  const methods = useForm<BulkSalaryIncreaseFormValues>({
    defaultValues: { data: [] },
  });

  const { control } = methods;

  // useFieldArray to manage table data
  const { fields, replace, append, update, insert, remove } = useFieldArray({
    control,
    name: "data",
  });

  const fetchAccumulations = async (
      filters: AccumulationsForEmploymentFilters,
  ): Promise<AccumulationsForEmploymentResponse | null> => {
    const accumulationsForEmployment = await dispatch(
        fetchAccumulationsForEmployment(filters),
    );
    if (isFulfilled(accumulationsForEmployment)) {
      return accumulationsForEmployment.payload;
    } else {
      console.error("failed to fetch accumulations", accumulationsForEmployment)
    }
    return null;
  };


  const applySalaryComponentAdjustment = useCallback(
      (
          originalValue: number,
          component: SalaryComponentRow | KtaComponentRow,
      ): number => {
      // override current salary with new value
      if (component.changeSalary && component.changeSalary.trim() !== "") {
        const overrideValue = Number(component.changeSalary);
        if (!isNaN(overrideValue)) {
          return round(overrideValue);
        }
      }
      // increase current salary by percentage
      if (
        component.increasePercentage &&
        component.increasePercentage.trim() !== ""
      ) {
        const percentage = Number(component.increasePercentage);
        if (!isNaN(percentage)) {
          return applyPercentageIncrease(originalValue, percentage);
        }
      }
      // increase current salary by amount
      if (component.increaseAmount && component.increaseAmount.trim() !== "") {
        const amount = Number(component.increaseAmount);
        if (!isNaN(amount)) {
          const newValue = originalValue + amount;
          return round(newValue);
        }
      }

      // if something went wrong, return original value
      return originalValue;
    },
    [],
  );


  // convert data for UI, salary increase values are calculated based on filters
  const setData = useCallback(
    (payload: SalaryRows, filters: BulkSalaryIncreaseFilterValues, unclearRows: Set<string>, doAppend = false) => {
      const pl: SalaryRow[] = Array.isArray(payload) ? payload : payload.data;
      const processedData: SalaryTableRows = pl.map(
        (salaryItem: SalaryRow): SalaryTableRow => {
          const tesTablesTarget =
            filters.target === BULK_SALARY_INCREASE_TARGETS.TES_TABLES;

          const { workTitleId } = salaryItem;
          const workTitle = {
            id: workTitleId,
            name: workTitleTranslations[workTitleId] || workTitleId,
          };

          const hrRepresentativeExpert = experts.find(
            (expert) => expert.id === salaryItem.hrRepresentative,
          );
          const hrRepresentative = hrRepresentativeExpert
            ? {
                id: hrRepresentativeExpert.id,
                name: getCoreUserFullName(hrRepresentativeExpert),
              }
            : null;

          const raiseSalaryObj = salaryItem.raiseDateSalary;
          const origSalary = raiseSalaryObj.salary;

          // Merge zonedSalary into salary
          // TODO: maybe would have made more sense to have salaryIncrease changed somehow rather than this "zone space salary" format
          let mergedSalary = {...origSalary};
          if (raiseSalaryObj.zonedSalary) {
            Object.entries(raiseSalaryObj.zonedSalary).forEach(([zone, zoneSalary]) => {
              Object.entries(zoneSalary).forEach(([code, value]) => {
                // console.log("adjusting mergedSalary", zone, code, value);
                // This is f*cked up with "Henkilökohtainen palkanosa" that we have in minimal, but luckily not in any real TES
                mergedSalary[`${zone} ${code}`] = value;
              });
            });
          }

          const salaryWarnings: SalaryWarning[] = [];
          if (salaryItem.afterRaiseSalaryPerDate.length > 0) {
            salaryWarnings.push({ type: SalaryWarningType.FUTURE_SALARIES });
          }
          const salaryComps = Object.keys(raiseSalaryObj.salary);

          // const nonSatisfyingComps: string[] = [];
          salaryComps.forEach(comp => {
            if (!tesTablesTarget && !filters.salaryComponents.some(fc => fc.code === comp)) {
              salaryWarnings.push({type: SalaryWarningType.INCOMPATIBLE_SALARY_COMPONENTS, params: {code: comp}});
              // nonSatisfyingComps.push(comp);
              // TODO maybe not the greatest idea to drop these from the object here
              // Let's hope there comes no such salary component that is not in filters
              // console.log(` deleting unused salary component ${comp}: not in filters: ${raiseSalaryObj.salary[comp]}`);
              // delete mergedSalary[comp];
            }
          });
          // if (nonSatisfyingComps.length > 0) {
          //   console.warn("Warning: Not all salary components are present in the filters. Some components may not shown or be adjusted correctly.");
          // }

          let salaryIncrease: Record<string, string> = {};
          if (tesTablesTarget) {
            salaryIncrease = { ...mergedSalary };
            if (raiseSalaryObj.previewSalary) {
              Object.entries(raiseSalaryObj.previewSalary).forEach(([code, value]) => {
                salaryIncrease[code] = value;
              });
            }
          }
          if (
            !tesTablesTarget &&
            filters.salaryIncreaseStartDate &&
            filters.salaryComponents &&
            filters.salaryComponents.length > 0 &&
            raiseSalaryObj
          ) {
            filters.salaryComponents.forEach(
              (component: SalaryComponentRow) => {
                if (Object.prototype.hasOwnProperty.call(raiseSalaryObj.salary, component.code)) {
                  const originalValue = Number(
                    raiseSalaryObj.salary[component.code],
                  );
                  if (!isNaN(originalValue)) {
                    const adjustedValue = applySalaryComponentAdjustment(
                      originalValue,
                      component,
                    );
                    salaryIncrease[component.code] = adjustedValue.toString();
                  } else {
                    salaryIncrease[component.code] = "";
                  }
                } else {
                  salaryIncrease[component.code] = "";
                }
                if (raiseSalaryObj.zonedSalary) {
                  const zonedSalary = raiseSalaryObj.zonedSalary;
                  Object.keys(zonedSalary).forEach((zone) => {
                    if (zonedSalary[zone].hasOwnProperty(component.code)) {
                      const originalValue = Number(
                        zonedSalary[zone][component.code],
                      );
                      if (!isNaN(originalValue)) {
                        const adjustedValue = applySalaryComponentAdjustment(
                          originalValue,
                          component,
                        );
                        salaryIncrease[`${zone} ${component.code}`] =
                          adjustedValue.toString();
                      } else {
                        salaryIncrease[`${zone} ${component.code}`] = "";
                      }
                    } else {
                      salaryIncrease[`${zone} ${component.code}`] = "";
                    }
                  });
                }
              },
            );
          }

          if (isEmploymentSalary(salaryItem)) {
            return {
              version: salaryItem.version,
              firstName: salaryItem.firstName,
              lastName: salaryItem.lastName,
              employmentId: salaryItem.employmentId,
              employerName: salaryItem.employerName,
              hrRepresentative,
              afterRaiseSalaryPerDate: salaryItem.afterRaiseSalaryPerDate,
              raiseDateSalary: {
                // there should be no zoned salaries in employments, but just for consistency
                ...raiseSalaryObj,
                salary: mergedSalary, // use merged salary here
              },
              salaryIncrease,
              status: SalaryIncreaseStatus.IDLE,
              raiseDateContEmpExists: salaryItem.raiseDateContEmpExists,
              talentId: salaryItem.talentId,
              warnings: salaryWarnings.length > 0 ? salaryWarnings : undefined,
              unclear: unclearRows.has(salaryItem.employmentId),
                workTitle,
            } as EmploymentSalaryRow;
          }

          return {
            version: salaryItem.version,
            talentId: salaryItem.talentId,
            firstName: salaryItem.firstName,
            lastName: salaryItem.lastName,
            workTitle,
            hrRepresentative,
            afterRaiseSalaryPerDate: salaryItem.afterRaiseSalaryPerDate,
            raiseDateSalary: {
              ...raiseSalaryObj,
              salary: mergedSalary, // use merged salary here
            },
            salaryIncrease,
            status: SalaryIncreaseStatus.IDLE,
            raiseDateContEmpExists: salaryItem.raiseDateContEmpExists,
            warnings: salaryWarnings.length > 0 ? salaryWarnings : undefined,
            unclear: unclearRows.has(rowToId(salaryItem)),
          } as ShiftSalaryRow;
        },
      );

      // Group EmploymentSalaryRows by talentId and salary
      if (filters.target === BULK_SALARY_INCREASE_TARGETS.EMPLOYMENT_SHIFTS) {
        const grouped: Record<string, EmploymentSalaryRow[]> = {};
        // const singles = Record<string, EmploymentSalaryRow[]> = {};
        processedData.forEach((row) => {
          if ("employmentId" in row && "raiseDateSalary" in row && row.talentId && row.raiseDateSalary) {
            const unclearKey = row.unclear ? "unclear-" : "";
            const key = `${unclearKey}${row.talentId}-${JSON.stringify(row.raiseDateSalary.salary)}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(row as EmploymentSalaryRow);
          }
        });

        const finalData: SalaryTableRows = [];
        Object.values(grouped).forEach((group) => {
          if (group.length > 1) {
            // Create GroupedEmploymentSalaryRow
            const first = {
              ...group[0],
              // groupedDate: group.map(r => r.raiseDateSalary.date).sort().join(", "),
              employerName: group.map(r => r.employerName)
                .filter(Boolean)
                .filter((name, index, self) => self.indexOf(name) === index)
                .join(", "),

            }

            finalData.push({
              ...first,
              // TODO do we want to mark grouped somehow so that they can be handled differently in the table?
              groupedEmploymentRows: group,
              expanded: false,
              uniqueWorkTitles: group.map(r => r.workTitle.name)
                .filter((name, index, self) => self.indexOf(name) === index)
                .join(", ")
            } as GroupedEmploymentSalaryRow);
          } else {
            finalData.push(group[0]);
          }
        });
        if (doAppend) {
          append(finalData);
        } else {
          replace(finalData);
        }
      } else {
        if (doAppend) {
          append(processedData);
        } else {
          replace(processedData);
        }
      }
    },
    [replace, append, workTitleTranslations, experts, applySalaryComponentAdjustment],
  );

  const setKtaData = useCallback(
      async (payload: Kta[], filters: BulkSalaryIncreaseFilterValues) => {
        const raiseDate = filters.salaryIncreaseStartDate as Date;
        const processedData: (KtaRow | undefined)[] = await Promise.all(
            payload.map(async (salaryItem: Kta): Promise<KtaRow | undefined> => {
              const hrRepresentativeExpert = experts.find(
                  (expert) => expert.id === salaryItem.hrRepresentative,
              );
              const hrRepresentative = hrRepresentativeExpert
                  ? {
                    id: hrRepresentativeExpert.id,
                    name: getCoreUserFullName(hrRepresentativeExpert),
                  }
                  : null;

              // console.log(
              //     "Fetching accumulations for employment",
              //     salaryItem.employmentId,
              // );

              let kta: Record<string, string> = {}
              // Fetch keskiansio accumulations for the employment
              const endDate = resolveKtaQueryEndDate(salaryItem);
              const accKeskiansio = await fetchAccumulations({
                employmentId: salaryItem.employmentId,
                scope: "EMPLOYMENT",
                stateNames: "keskiansio",
                from: salaryItem.startDate,
                to: endDate,
              });

              let keskiansio = "";
              let currentKeskiansio = "";
              let salaryIncrease: Record<string, string> = {};
              let status = SalaryIncreaseStatus.IDLE;
              if (accKeskiansio) {
                // console.log("Keskiansio accumulations fetched", accKeskiansio);
                outer: for (const item of accKeskiansio.data?.reverse()) {
                  // console.log("Keskiansio Accumulation item", item);

                  const isRaiseDateItem = isEqual(item.date!, raiseDate);
                  for (const row of item.rows) {
                    // console.log("Keskiansio Accumulation row", row);
                    currentKeskiansio = row["keskiansio"];
                    if (isRaiseDateItem && row.deltaId !== null) {
                      // console.log("Keskiansio Accumulation delta already found for raise date", row);
                      status = SalaryIncreaseStatus.DONE;
                    }

                    const component = filters.ktaKeskiansioComponents[0];
                    if (keskiansio === "") {
                      keskiansio = applyKtaComponentAdjustment(Number(currentKeskiansio), component).toFixed(8);
                      salaryIncrease["kta"] = keskiansio;
                      salaryIncrease["ktadate"] = item.date!;
                      break outer;
                    }
                  }
                }
              }

                if (!salaryIncrease["ktadate"]) {
                    // console.log("warn no kta found");
                }

              // Fetch keskiansiolaskenta accumulations for the employment
              const acc = await fetchAccumulations({
                employmentId: salaryItem.employmentId,
                scope: "EMPLOYMENT",
                stateNames: "keskiansiolaskenta",
                from: salaryItem.startDate,
                to: endDate,
              });

              let raiseDateKta: AccumDateData | undefined;
              if (acc) {
                // console.log("Accumulations fetched", acc);
                for (const item of acc.data?.reverse()) {
                  // console.log("Accumulation item", item);

                  item.rows.forEach((row) => {
                    // console.log("Accumulation row", row);
                    const ka =
                        row.keskiansiolaskenta as AccumulationCalculationMultiple;
                    const avgs = ka.ongoingAverages;
                    if (avgs) {
                      Array.from(Object.keys(avgs))
                          .sort()
                          .reverse()
                          .forEach((key) => {
                            const avg = avgs[key];
                            // console.log("Accumulation ongoing average", key, avg);
                          });
                    }
                  });

                  if (!isAfter(item.date as string, raiseDate)) {
                    // console.log("Correct data found", item);
                    raiseDateKta = item;
                    break;
                  }
                }
              }

              let raisedKta: KtaAvg = {};
              let currentAvg: KtaAvg = {};
              if (raiseDateKta && filters.ktaComponents) {
                for (const component of filters.ktaComponents) { //.forEach((component) => {
                  //if (raiseDateKta.date == component.date) {
                  // const delta = raiseDateKta.rows.findLast(r => !!r.deltaId)

                  const r = latestAccumRow(raiseDateKta!);
                  if (r.deltaId && isEqual(raiseDateKta.date!, raiseDate)) {
                    // console.log("delta", r.deltaId);
                    status = SalaryIncreaseStatus.DONE;
                    // just mark it done, no need to cancel because we might want see done ones (this is how the filter works now)
                  } else {
                    // TODO try to find the correct one..? Are they always in order
                  }
                  const avgs = (
                      r.keskiansiolaskenta as AccumulationCalculationMultiple
                  ).ongoingAverages;

                  if (avgs) {
                    currentAvg = avgs as KtaAvg;
                    const key = component.date as keyof KtaAvg;
                    const val = avgs[key];
                    if (val) {
                      kta[key] = (Number(val.salary) / Number(val.workTime)).toFixed(8);
                      let [nval, nhours] = applyKtaOngoingComponentAdjustment(
                          parseFloat(val.salary),
                          parseFloat(val.workTime),
                          component,
                      );
                      // console.log("nval", nval, nhours);
                      salaryIncrease[key] = (nval / nhours).toFixed(8);
                      //raisedKta[key] = [nval, nhours];
                      raisedKta[key] = {
                        valueDate: val.valueDate,
                        salary: nval.toFixed(8),
                        workTime: nhours.toFixed(8),
                      };
                    }
                  }
                }

                return {
                  firstName: salaryItem.firstName,
                  lastName: salaryItem.lastName,
                  employmentId: salaryItem.employmentId,
                  employerName: salaryItem.employerName,
                  hrRepresentative,
                  status: status,
                  talentId: salaryItem.talentId,
                  raiseDateKta: raiseDateKta,
                  startDate: salaryItem.startDate,
                  endDate: salaryItem.endDate,
                  contEmpId: salaryItem.contEmpId,
                  salaryIncrease,
                  raisedKta,
                  raisedKtaDate: raiseDateKta!.date!,
                  currentKta: kta,
                  currentOngoingKta: currentAvg,
                  currentKeskiansio: currentKeskiansio,
                  raisedKeskiansio: keskiansio
                } as KtaRow;
              } else {
                // console.warn("nothingto raise");
              }
            }),
        );

        const ktaRows = processedData.filter((pd): pd is KtaRow => !!pd);

        switch (filterValues.isIncreaseApplied) {
          case "true":
            replace(ktaRows.filter((pd) => pd.status === SalaryIncreaseStatus.DONE));
            break;
          case "false":
            replace(ktaRows.filter((pd) => pd.status === SalaryIncreaseStatus.IDLE));
            break;
          case "n/a":
          case undefined:
            replace(ktaRows);
            break;
          default:
            console.error("Unknown increase filter:", filterValues.isIncreaseApplied);
            replace(ktaRows);
        }
      },
      // FIXME: fix this or add comment why this is needed
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isKta, experts, fetchAccumulations, filterValues.isIncreaseApplied, replace],
  );

  const getUniqueKtaDates = useCallback(() => {
    const allKtas = fields.filter((item) =>
        isKtaRow(item),
    ) as unknown as KtaRow[];
    const allKtaDates = allKtas.flatMap((item) => {
      if (item.raiseDateKta) {
        const ret = item.raiseDateKta.rows
            .map((row) => {
              return Object.keys(
                  (row.keskiansiolaskenta as AccumulationCalculationMultiple)
                      .ongoingAverages!!,
              );
            })
            .flat()
            .sort();
        return ret;
      } else {
        return [""];
      }
    });
    return [...new Set(allKtaDates)];
  }, [fields]);

  const uniqueSalaryKeysByType = useMemo(
    () => {
      if (isKta) {
        return {
          [GetUniqueSalaryKeysCallbackType.RAISE_DATE_SALARY]: [],
          [GetUniqueSalaryKeysCallbackType.AFTER_RAISE_SALARY_PER_DATE]: [],
        };
      }

      return {
        [GetUniqueSalaryKeysCallbackType.RAISE_DATE_SALARY]: [
          ...flatMapAsSet(fields, (item2) => {
            const item = item2 as EmploymentSalaryRow | ShiftSalaryRow;
            return resolveSalaryKeys(item.raiseDateSalary);
          }),
        ],
        [GetUniqueSalaryKeysCallbackType.AFTER_RAISE_SALARY_PER_DATE]: [
          ...flatMapAsSet(fields, (item) => {
            if (
              (isEmploymentSalaryTableRow(item) ||
              isShiftSalaryTableRow(item)) && item.afterRaiseSalaryPerDate.length > 0
            ) {
              return item.afterRaiseSalaryPerDate.flatMap((salary) =>
                resolveSalaryKeys(salary)
              );
            }
            return [];
          }),
        ],
      };
    },
    [fields, isKta],
  );

  const getUniqueSalaryKeys: GetUniqueSalaryKeysCallback = useCallback(
    (type) => uniqueSalaryKeysByType[type],
    [uniqueSalaryKeysByType],
  );

  const getHeaders = useCallback(() => {
    const baseHeaders = {
      SHIFT_SALARIES: [
        "Tehtävänimike",
        "Voimaantulopäivämäärä",
        "Palkkataulukko",
        "Nippu korotuspäivänä",
      ],
      TES_TABLES: [
        "Tehtävänimike",
        "Voimaantulopäivämäärä",
        "Palkkataulukko",
        "Nippu korotuspäivänä",
      ],
      KTA: ["Voimaantulopäivämäärä", "Eezy-yhtiö", `Keskiansio`, "Keskiansio korotettu"],
      KTA_SINGLE: ["Voimaantulopäivämäärä", "Eezy-yhtiö"],
      DEFAULT: ["Tehtävänimike", "Voimaantulopäivämäärä", "Palkkataulukko", "Nippu korotuspäivänä", "Eezy-yhtiö"],
    };

    const headersByTarget =
        baseHeaders[filterValues.target as keyof typeof baseHeaders] ||
        baseHeaders.DEFAULT;

    if (isKta) {
      const ktaDates = getUniqueKtaDates();
      // console.log("KTA dates", ktaDates);
      return [
        "Nimi",
        ...headersByTarget,
        ...ktaDates.flatMap((k) => [`${k} nykyinen`, `${k} korotettu`]),
        "Tila",
        "Epäselvä",
        "Oma HR",
      ];
    }

    const uniqueSalaryKeys = getUniqueSalaryKeys(
      GetUniqueSalaryKeysCallbackType.RAISE_DATE_SALARY,
    );

    const salaryHeaders = uniqueSalaryKeys.flatMap((key) => [
      salaryTranslations[key] ? `${salaryTranslations[key]} (${key})` : key,
      `${key} ${filterValues.salaryIncreaseStartDate ? format(filterValues.salaryIncreaseStartDate, "dd.MM.yyyy") : ""}`,
    ]);

    return ["", "Nimi", ...headersByTarget, ...salaryHeaders, "Tila", "Epäselvä", "Oma HR"];
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getUniqueSalaryKeys,
    filterValues.target,
    filterValues.salaryIncreaseStartDate,
    salaryTranslations,
  ]);

  const findRowIndexById = useCallback(
    (id: string): number => {
      if (filterValues.target === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES || filterValues.target === BULK_SALARY_INCREASE_TARGETS.KTA) {
        return fields.findIndex((item) => item.id === id);
      } else {
        return fields.findIndex((item) => (item as EmploymentSalaryRow).employmentId === id);
      }
    },
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields],
  );

  const findRowIndex = useCallback(
    (row: SalaryTableRow): number => {
      if (filterValues.target === BULK_SALARY_INCREASE_TARGETS.SHIFT_SALARIES || filterValues.target === BULK_SALARY_INCREASE_TARGETS.KTA) {
        return fields.findIndex((item) => item.id === row.id);
      } else {
        const row_ = row as EmploymentSalaryRow;
        return fields.findIndex((item) => (item as EmploymentSalaryRow).employmentId === row_.employmentId);
      }
      // if (filterValues.target === BULK_SALARY_INCREASE_TARGETS.EMPLOYMENT) {
      //   const row = row as EmploymentSalaryRow;
      //   return fields.findIndex((item) => item.employmentId === row.employmentId);
      // }
      // return fields.findIndex((item) => {
      //   // TODO: all of these can be item.id === row.id
      //   if (isGroupedEmploymentSalaryRow(item) && isGroupedEmploymentSalaryRow(row)) {
      //     //item.employmentRows.map(er => er.id)
      //     return item.id === row.id;
      //   }
      //   if (isShiftSalaryTableRow(item) && (row)) {
      //     return item.id === row.id;
      //   }
      //   if (
      //     isEmploymentSalaryTableRow(item) &&
      //     isEmploymentSalaryTableRow(row)
      //   ) {
      //     return item.employmentId === row.employmentId;
      //   }
      //   if (isKtaRow(row)) {
      //     return item.id === row.id;
      //   }
      //   return false;
      // });
    },
    [fields, filterValues.target],
  );

  const updateRow = useCallback(
    (row: SalaryTableRow, field: keyof ShiftSalaryRow, value: any) => {
      const index = findRowIndex(row);
      // console.log("UPDATE ROW", index, row, field, value);
      if (index !== -1) {
        // update version and specific field
        const updatedRow = {
          ...fields[index],
          ...(fields[index]?.version && {version: fields[index].version + 1}),
          [field]: value,
        };
        update(index, updatedRow);
      }
    },
    [fields, findRowIndex, update],
  )

  const updateRowSuccess = useCallback(
    (row: GroupedEmploymentSalaryRow) => {
      const index = findRowIndex(row);
      // console.log("UPDATE ROW SUCCESS", index, row);
      if (index !== -1) {
        // update version and specific field
        const originalGroup = fields[index] as GroupedEmploymentSalaryRow;
        const updatedGroupedEmploymentRows = originalGroup.groupedEmploymentRows.map((item, idx) =>
          { return {...item, ...(item?.version && {version: item.version + 1}), status: SalaryIncreaseStatus.SUCCESS }}
        );

        // console.log("Marking grouped row success", row, JSON.stringify(updatedGroupedEmploymentRows, null, 2));
        const updatedRow = {
          ...fields[index],
          ...(fields[index]?.version && {version: fields[index].version + 1}),
          groupedEmploymentRows: updatedGroupedEmploymentRows,
          status: SalaryIncreaseStatus.SUCCESS,
        };
        update(index, updatedRow);
      }
    },
    [fields, findRowIndex, update],
  )

  const updateGroupedRow = useCallback(
    (group: GroupedEmploymentSalaryRow, row: EmploymentSalaryRow, field: keyof ShiftSalaryRow, value: any) => {
      const index = findRowIndex(group);
      // console.log("UPDATE ROW", index, row, field, value);
      if (index !== -1) {
        // update version and specific field
        // TODO fix when we have grouped shift rows
        const originalGroup = fields[index] as GroupedEmploymentSalaryRow;
        const updatedGroupedEmploymentRows = originalGroup.groupedEmploymentRows.map((item, idx) =>
          item.employmentId === row.employmentId ? { ...item, ...(item?.version && {version: item.version + 1}), [field]: value } : item
        );
        const updatedRow = {
          ...fields[index],
          // ...(fields[index]?.version && {version: fields[index].version + 1}),
          groupedEmploymentRows: updatedGroupedEmploymentRows,
        };
        // console.log("Before update:", JSON.stringify(fields[index], null, 2), fields[index] === updatedRow);
        // console.log("UpdatedOne:", JSON.stringify(updatedRow, null, 2), fields[index] === updatedRow);
        update(index, updatedRow);
        // setTimeout(() => {
        //   console.log("After update:", fields[index]);
        // }, 0);
      }
    },
    [fields, findRowIndex, update],
  )


  const updateRowClarity = useCallback(
    (rowId: string, value: boolean) => {
      const index = findRowIndexById(rowId);
      // console.log("UPDATE ROW", index, row, field, value);
      if (index !== -1) {
        let row = {...fields[index]};
        if (isGroupedEmploymentSalaryRow(row)) {
          if (!row.expanded) {
            row.groupedEmploymentRows.forEach(r => r.unclear = value);
          }
          // console.log("new rows", row.groupedEmploymentRows);
        }
        // update version and specific field
        const updatedRow = {
          ...row,
          unclear: value,
        };
        update(index, updatedRow);
      }
    },
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, findRowIndex, update],
  )

  const toggleExpandedRow = useCallback(
    (row: GroupedSalaryTableRow)=> {
      // TODO VK cleanup
    // console.log("toggleExpandedRow", row);
    const index = findRowIndex(row);
    // console.log("row idx", index);
    row.expanded = !row.expanded;
    update(index, row);
    if (row.expanded) {
      let addedGroups = isGroupedEmploymentSalaryRow(row) ? row.groupedEmploymentRows : row.groupedShiftRows;
      insert(index + 1, addedGroups.slice(1))
    } else {
      const leng = isGroupedEmploymentSalaryRow(row) ? row.groupedEmploymentRows.length - 1 : row.groupedShiftRows.length - 1
      let removedIndexes = Array.from({ length: leng }, (_, i) => index + 1 + i)
      remove(removedIndexes)
    }
    // FIXME: fix this or add comment why this is needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, update, insert, remove])

  return {
    methods,
    fields,
    setData,
    setKtaData,
    updateRow,
    updateGroupedRow,
    updateRowSuccess,
    getHeaders,
    getUniqueSalaryKeys,
    getUniqueKtaDates,
    fetchAccumulations,
    toggleExpandedRow,
    updateRowClarity,
  };
};


export default useBulkSalaryData;
